import cn from 'classnames'
import { goToChat } from 'utils/go-to-chat'

import { useState, MouseEvent, useEffect, useRef } from 'react'

import { useTranslation } from 'utils/use-translation'

import { formatNumber } from 'utils/formatters'

import DiscountCheckpoint from '../discount-checkpoint'

import { discountData } from '../../data'

import { DiscountArgs, DiscountInfoProps, DiscountLevel } from '../../loyalty-program.props'

import styles from './styles.module.scss'

const DiscountInfo = ({ isKycVerified, discountLevel }: DiscountInfoProps) => {
  const [activeTooltipIndex, setActiveTooltipIndex] = useState(null)
  const [activeAnimatedTooltipIndex, setAnimatedTooltipIndex] = useState(null)
  const { firstCheckpoint, secondCheckpoint } = discountData
  const timeoutRef = useRef(null)
  const { t, replacePlaceholders } = useTranslation()

  const isCheckpointChecked = {
    0: discountLevel !== DiscountLevel.FIRST,
    1: discountLevel === DiscountLevel.THIRD,
  }

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target
    if (target instanceof HTMLElement && target.tagName === 'BUTTON') {
      goToChat()
    }
  }

  const getDiscountPercent = (discount: DiscountArgs) => {
    const { isKycVerified, checkpointIndex, discountLevel } = discount || {}

    const checkpoints = {
      0: discountData.firstCheckpoint,
      1: discountData.secondCheckpoint,
    }

    const { percent, kycPercent } = checkpoints[checkpointIndex] || {}

    if (checkpointIndex === 1 && isKycVerified && discountLevel !== 'first') {
      return kycPercent
    }
    return isKycVerified ? kycPercent : percent
  }

  const getDiscountValue = (discountData: DiscountArgs): string => {
    return `<span>${getDiscountPercent(discountData)}%</span>`
  }

  const getDiscountText = (discountData: DiscountArgs): string => {
    const { checkpointIndex, discountLevel } = discountData || {}

    if (discountLevel === 'third') return 'contact_us_to_get_discount'
    if (discountLevel === 'second' && checkpointIndex === 0) return 'contact_us_to_get_discount'
    return 'discount_you_can_get'
  }

  const getUnverifiedKycText = (discountPercent: number): string => {
    return `${t('increase_discount')} <span>${discountPercent}%</span>.`
  }

  const getTooltipText = (isKycVerified: boolean, checkpointIndex: number): string => {
    const discountProps = {
      isKycVerified,
      checkpointIndex,
      discountLevel,
    }
    const discountText = getDiscountText(discountProps)
    const discountValue = getDiscountValue(discountProps)

    const percent =
      checkpointIndex === 0 ? discountData.secondCheckpoint.percent : discountData.secondCheckpoint.kycPercent
    const increaseDiscountText = !isKycVerified ? getUnverifiedKycText(percent) : ''

    return `<p>${replacePlaceholders(t(discountText), {
      contact_us: `<button>${t('contact_us_capital')}</button>`,
      value: formatNumber(checkpointIndex === 0 ? firstCheckpoint.sum : secondCheckpoint.sum),
      percent: discountValue,
    })}.</p> <p>${increaseDiscountText}</p>`
  }

  const handleMouseEnter = (index: number) => {
    if (index !== activeTooltipIndex) {
      clearTimeout(timeoutRef.current)
      setActiveTooltipIndex(index)
      timeoutRef.current = setTimeout(() => {
        setAnimatedTooltipIndex(index)
      }, 10)
    }
  }

  const handleMouseLeave = () => {
    setAnimatedTooltipIndex(null)
    timeoutRef.current = setTimeout(() => {
      setActiveTooltipIndex(null)
    }, 200)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <div>
      {[0, 1].map((_, index) => (
        <DiscountCheckpoint
          key={index}
          className={styles.desktop__checkpoint}
          isChecked={isCheckpointChecked[index]}
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={() => handleMouseLeave()}
          style={{ zIndex: activeTooltipIndex !== index && index === 0 ? '101' : '' }}
        >
          {index === activeTooltipIndex && (
            <div
              className={cn(styles['desktop__checkpoint-wrapper'], {
                [styles.show]: index === activeAnimatedTooltipIndex,
              })}
            >
              <div
                onClick={handleClick}
                className={cn(styles.tooltip)}
                dangerouslySetInnerHTML={{ __html: getTooltipText(isKycVerified, index) }}
              />
            </div>
          )}
        </DiscountCheckpoint>
      ))}
    </div>
  )
}

export default DiscountInfo
